import {
  styled,
  Box,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";

const PRIMARY = appThemeColors.text.lightBlue;
const PRIMARY_HOVER = appThemeColors.text.heading;
const AUTH_FONT = appThemeFonts.family.ui;
const SUCCESS_TEXT = appThemeColors.status.ok.color;
const AUTH_FIELD_BG = appThemeColors.background.tint;
const AUTH_CARD_SHADOW =
  "0 12px 24px rgba(10, 57, 129, 0.12), 0 4px 8px rgba(10, 57, 129, 0.06)";
const AUTH_FOCUS_RING = "0 0 0 3px rgba(11, 95, 165, 0.35)";

export const StyledPage = styled(Box)({
  position: "relative",
  height: "100dvh",
  maxHeight: "100dvh",
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: AUTH_FIELD_BG,
  fontSize: appThemeFonts.size.lg,
  fontFamily: AUTH_FONT,
});

/** Main auth area — signup/reset card is centered here. */
export const StyledAuthMain = styled(Box)({
  flex: "1 1 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 28px",
  minHeight: 0,
  overflow: "hidden",
  boxSizing: "border-box",
});

export const StyledAuthFooter = styled(Box)({
  flexShrink: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: appThemeColors.background.soft,
  padding: "10px 16px",
  minHeight: 52,
  boxSizing: "border-box",
});

export const StyledInframarkFooterLogo = styled("img")({
  height: 40,
  width: "auto",
  maxWidth: "min(280px, 90vw)",
  objectFit: "contain",
  display: "block",
});

export const StyledContainer = styled(Box)({
  width: "100%",
  maxWidth: 580,
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  justifyContent: "center",
});

export const StyledResetContainer = styled(Box)({
  width: "100%",
  maxWidth: 440,
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  justifyContent: "center",
});

export const StyledCard = styled(Box)({
  width: "100%",
  padding: "28px",
  background: appThemeColors.background.paper,
  borderRadius: 14,
  boxShadow: AUTH_CARD_SHADOW,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  boxSizing: "border-box",
});

export const StyledAuthCardHeader = styled(Box)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
});

export const StyledSignupContainer = styled(Box)({
  maxWidth: 760,
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  justifyContent: "center",
});

export const StyledSignupCard = styled(StyledCard)({
  maxHeight: "calc(100dvh - 52px - 3rem)",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  alignItems: "stretch",
  padding: "28px",
});

export const StyledSignupSuccessCard = styled(StyledCard)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: "28px",
});

export const StyledAuthSuccessRedirectMessage = styled(Typography)({
  margin: 0,
  fontSize: appThemeFonts.size.base,
  fontFamily: AUTH_FONT,
  color: appThemeColors.text.muted,
  lineHeight: 1.5,
  textAlign: "center",
  width: "100%",
});

export const StyledSignupCardHeader = styled(Box)({
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "& h2, & p": {
    alignSelf: "stretch",
  },
});

export const StyledWatermindsLogo = styled("img")({
  width: "auto",
  height: 50,
  maxWidth: "100%",
  display: "block",
  margin: "0 auto 12px",
});

export const StyledInframarkLogo = styled("img")({
  margin: "10px auto 20px auto",
  width: "50%",
  maxWidth: "100%",
});

export const StyledTitle = styled(Typography)({
  margin: "0 0 4px",
  fontSize: appThemeFonts.size.display,
  fontWeight: appThemeFonts.weight.semibold,
  fontFamily: AUTH_FONT,
  color: appThemeColors.text.heading,
  width: "100%",
  textAlign: "center",
  lineHeight: 1.3,
  "&.MuiTypography-h5": {
    fontWeight: appThemeFonts.weight.semibold,
  },
});

export const StyledSubtitle = styled(Typography)({
  margin: "0 0 14px",
  fontSize: appThemeFonts.size.basePlus,
  fontFamily: AUTH_FONT,
  color: appThemeColors.text.muted,
  width: "100%",
  textAlign: "center",
  lineHeight: 1.45,
});



export const StyledForm = styled("form")({
  width: "100%",
});

export const StyledAuthForm = styled("form")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  margin: 0,
});

export const StyledAuthBackLinkWrap = styled(Box)({
  margin: "14px 0 0",
  width: "100%",
});

export const StyledSignupForm = styled(StyledForm)({
  minHeight: 0,
  display: "flex",
  flexDirection: 'column',
  gap: '24px',
  overflow: "hidden",
});

export const StyledSignupFieldsScroll = styled(Box)(({ theme }) => ({
  minHeight: 0,
  width: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  scrollPaddingTop: theme.spacing(2.5),
  "& > div": {
    marginBottom: 0,
  },
  "& .MuiFormControl-root": {
    overflow: "visible",
  },
}));

export const StyledSignupFormFooter = styled(Box)({
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
});

export const StyledSignupActions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
});

export const StyledPasswordHint = styled(Typography)({
  margin: "0 0 8px",
  fontSize: appThemeFonts.size.md,
  color: appThemeColors.text.muted,
  fontWeight: appThemeFonts.weight.regular,
  lineHeight: 1.35,
});

export const StyledFieldHint = styled(Typography)({
  margin: "4px 0 16px",
  fontSize: appThemeFonts.size.md,
  color: appThemeColors.text.muted,
  fontWeight: appThemeFonts.weight.regular,
  lineHeight: 1.35,
  width: "100%",
});

export const StyledErrorMessage = styled(Typography)({
  margin: 0,
  padding: 12,
  fontSize: appThemeFonts.size.lg,
  borderRadius: 4,
  textAlign: "center",
  color: appThemeColors.semantic.danger,
});

export const StyledAuthSrOnly = styled("span")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
});

export const StyledAuthSuccessBand = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 8,
  margin: "0 0 12px",
  padding: "12px 14px",
  borderRadius: 10,
  background: appThemeColors.status.ok.background,
});

export const StyledAuthSuccessMark = styled("span")({
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  fontSize: 13,
  lineHeight: 1.45,
  color: appThemeColors.status.ok.color,
});

export const StyledAuthSuccessBandText = styled(Typography)({
  margin: 0,
  display: "inline",
  fontSize: appThemeFonts.size.basePlus,
  fontWeight: appThemeFonts.weight.semibold,
  fontFamily: AUTH_FONT,
  color: appThemeColors.text.primary,
  lineHeight: 1.45,
  minWidth: 0,
});

export const StyledAuthSuccessBody = styled(Typography)({
  margin: "0 0 18px",
  fontSize: appThemeFonts.size.base,
  fontFamily: AUTH_FONT,
  color: appThemeColors.text.muted,
  lineHeight: 1.5,
  textAlign: "center",
  width: "100%",
});

export const StyledSignupSuccessBand = styled(StyledAuthSuccessBand)({
  margin: "0 0 16px",
});

export const StyledSubmitButton = styled(Button)({
  width: "100%",
  minHeight: 48,
  padding: "0 12px",
  fontSize: appThemeFonts.size.lg,
  fontWeight: appThemeFonts.weight.semibold,
  fontFamily: AUTH_FONT,
  color: appThemeColors.neutral.white,
  background: PRIMARY,
  textTransform: "none",
  borderRadius: 10,
  boxShadow: "none",
  "&:hover": {
    background: PRIMARY_HOVER,
    boxShadow: "none",
  },
  "&:disabled": {
    opacity: 0.6,
  },
});

export const StyledSignupSubmitButton = styled(StyledSubmitButton)({
  width: "auto",
  minWidth: "unset",
  padding: "0 18px",
  height: '38px !important',
  minHeight: '38px !important',
  borderRadius: "22px",
});

export const StyledAuthTextLink = styled(Button)({
  display: "block",
  width: "100%",
  marginTop: 0,
  padding: 0,
  minHeight: "auto",
  textTransform: "none",
  textDecoration: "underline",
  color: PRIMARY,
  fontWeight: appThemeFonts.weight.regular,
  fontFamily: AUTH_FONT,
  fontSize: appThemeFonts.size.base,
  textAlign: "center",
  "&:hover": {
    background: "transparent",
    textDecoration: "underline",
    color: appThemeColors.text.heading,
  },
});

export const StyledAuthFormFields = styled(Box)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  "& > div:not([data-auth-columns])": {
    display: "flex",
    flexDirection: "column",
    margin: 0,
  },
  "& > div > *": {
    marginBottom: "0 !important",
  },
  "& .MuiFormControl-root": {
    margin: 0,
    width: "100%",
  },
});

export const StyledTextField = styled(TextField)({
  width: "100%",
  marginBottom: 20,
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PRIMARY,
      borderWidth: 1,
    },
  },
});

export const StyledSecondaryButton = styled(Button)({
  width: "100%",
  marginTop: 14,
  minHeight: 48,
  padding: "0 12px",
  textTransform: "none",
  fontFamily: AUTH_FONT,
  fontSize: appThemeFonts.size.lg,
  fontWeight: appThemeFonts.weight.semibold,
  borderRadius: 10,
  color: appThemeColors.text.primary,
  borderColor: appThemeColors.border.strong,
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
    borderColor: PRIMARY,
  },
});

export const StyledSuccessMessage = styled(Typography)({
  margin: "0 0 20px",
  color: SUCCESS_TEXT,
  fontFamily: AUTH_FONT,
  textAlign: "center",
  lineHeight: 1.45,
});

export const StyledPhoneFieldWrapper = styled(Box)({
  width: "100%",
  marginBottom: 20,
});

export const SignupTwoColumnRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
  width: "100%",
  alignItems: "start",
  "@media (max-width: 520px)": {
    gridTemplateColumns: "1fr",
  },
});

SignupTwoColumnRow.defaultProps = {
  "data-auth-columns": "",
};

export const SignupNameFieldsRow = SignupTwoColumnRow;
